import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { ApiError } from '@crave/api-client';
import { Button, Card } from '@crave/ui';
import { api } from '../api.ts';
import { FeedbackForm } from '../components/Feedback.tsx';

interface Sample {
  /** A slip handed to one person answers once; the batch QR on the table answers for everyone. */
  kind?: 'sample' | 'batch';
  sample_code: string;
  campaign: string;
  item_name: string;
  submitted: boolean;
  /** False once the owner closes feedback for this tasting. */
  open: boolean;
}

/** Reached by scanning the QR code on a tasting sample. No sign-in. */
export function TastePage() {
  const { code = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['taste', code],
    queryFn: async () => (await api.get<Sample>(`/taste/${encodeURIComponent(code)}`)).data,
    retry: false,
  });
  if (q.isError)
    return (
      <Card>
        <p style={{ margin: 0 }}>
          {q.error instanceof ApiError ? q.error.message : "We couldn't load that sample."}
        </p>
      </Card>
    );
  if (!q.data) return <p>Loading…</p>;
  const s = q.data;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          Tasting · {s.campaign} · {s.kind === 'batch' ? 'batch' : 'sample'} {s.sample_code}
        </div>
        <h1 style={{ margin: '4px 0 0' }}>You tried the {s.item_name}.</h1>
        {!s.submitted && s.open && (
          <p style={{ margin: '4px 0 0', color: 'var(--ink-soft)' }}>
            Tell us straight. It takes twenty seconds, and no sign-in.
          </p>
        )}
      </div>
      {s.submitted ? (
        <Card>
          <strong>Thanks, we already have feedback for this sample.</strong>
        </Card>
      ) : !s.open ? (
        <Card>
          <strong>Feedback for this tasting is closed.</strong>
          <p style={{ margin: '6px 0 0', color: 'var(--ink-soft)' }}>Thanks for trying it.</p>
        </Card>
      ) : (
        <FeedbackForm
          title="How was it?"
          askName
          askEmail
          onSubmit={async (body) => {
            try {
              await api.post(`/taste/${encodeURIComponent(code)}/feedback`, body);
            } catch (e) {
              // Closed or already rated since the page loaded: refresh so the right card shows.
              if (
                e instanceof ApiError &&
                (e.code === 'TASTING_CLOSED' || e.code === 'ALREADY_SUBMITTED')
              )
                void qc.invalidateQueries({ queryKey: ['taste', code] });
              throw e;
            }
          }}
        />
      )}
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Button variant="secondary">See the menu</Button>
      </Link>
    </div>
  );
}
